/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import AssignCoursePanel from "../../components/assign-course-panel";
import AssignMembershipCard from "../../components/assign-membership-card";
import {
  useEnrollmentById,
  useOnboardEnrollment,
} from "../../hooks/useEnrollment";
import { formatDate, initials, titleCase } from "../../lib/helpers.lib";

export default function EnrollmentDetails() {
  const { id } = useParams();
  const { data, isLoading } = useEnrollmentById(id);
  const onboard = useOnboardEnrollment();
  const e = data?.data || {};

  const paid = Boolean(e?.hasMadeOnboardingPayment);
  const amount = Number(e?.onboardingPaymentAmount || 0);
  const created = e?.createdAt ? new Date(e.createdAt) : null;
  const motivation = Array.isArray(e?.motivationReasons)
    ? e.motivationReasons
    : [];
  const hasPartner = Boolean(
    e?.partner || e?.partnerLocation || typeof e?.isPartnerActive === "boolean",
  );

  const sections = useMemo(
    () => [
      {
        title: "Personal",
        items: [
          ["Registration number", e.registrationNumber],
          ["First name", e.firstName],
          ["Last name", e.lastName],
          ["Gender", titleCase(e.gender)],
          ["Date of birth", formatDate(e.dateOfBirth)],
          ["Address", e.address],
          ["Email", e.email],
          ["Phone", e.phone],
        ],
      },
      {
        title: "Education",
        items: [
          ["Highest qualification", e.highestQualification],
          ["Institution", e.institution],
          ["Department/Course", e.departmentOfStudy],
          ["Graduation year", e.graduationYear],
        ],
      },
      {
        title: "Employment",
        items: [
          ["Current position", e.currentPosition],
          ["Years of experience", e.yearsOfExperience],
          ["Current employer", e.currentEmployer],
          ["Industry", e.industry],
          ["Department/SBU", e.departmentSBU],
        ],
      },
      {
        title: "Course & Payment",
        items: [
          ["Membership Type", e.membershipTitle],
          ["Paid", paid ? "Yes" : "No"],
          ["Amount", `₦${amount.toLocaleString("en-NG")}`],
          ["Payment reference", e.onboardingPaymentReference || "—"],
          ["Joined", created ? created.toLocaleDateString() : "—"],
        ],
      },
    ],
    [e, paid, amount, created],
  );

  if (isLoading) return <PageSkeleton />;

  const handleOnboard = async () => {
    try {
      const out = await onboard.mutateAsync(id);
      toast.success(out?.message || "Onboarding Email sent.");
      const reg = out?.data?.registrationNumber;
      if (reg) toast.message("Registration Number", { description: reg });
    } catch (err) {
      const message =
        err?.response?.data?.message || "Failed to onboard member";
      toast.error(message);
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-700 text-lg font-semibold">
            {initials(`${e.firstName || ""} ${e.lastName || ""}`)}
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-semibold">
                {e.firstName} {e.lastName}
              </h1>
              <span className="inline-flex items-center rounded border border-slate-200 bg-white px-2 py-0.5 text-xs font-medium uppercase tracking-wide">
                {e.courseSlug || "—"}
              </span>
              <span
                className={
                  "inline-flex items-center rounded px-2 py-0.5 text-xs font-medium " +
                  (paid ? "bg-green-600 text-white" : "bg-red-600 text-white")
                }
              >
                {paid ? "Paid" : "Unpaid"}
              </span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-slate-500">
              {e.email && (
                <a className="hover:underline" href={`mailto:${e.email}`}>
                  {e.email}
                </a>
              )}
              {e.email && e.phone && <span>•</span>}
              {e.phone && (
                <a className="hover:underline" href={`tel:${e.phone}`}>
                  {e.phone}
                </a>
              )}
              {created && (
                <>
                  <span>•</span>
                  <span>Joined {created.toLocaleDateString()}</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <Link
            to="/enrollments"
            className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50"
          >
            ← Back
          </Link>

          <button
            onClick={handleOnboard}
            disabled={onboard.isLoading}
            className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            {onboard.isLoading ? "Onboarding..." : "Onboard Member"}
          </button>

          {e.cv ? (
            <a
              href={e.cv}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-black"
            >
              ⬇ Download CV
            </a>
          ) : (
            <button
              disabled
              className="inline-flex items-center gap-2 rounded-md bg-slate-200 px-3 py-2 text-sm font-medium text-slate-500"
            >
              No CV
            </button>
          )}
        </div>
      </header>

      <div className="grid gap-6 md:grid-cols-3">
        <div className="md:col-span-2 space-y-6">
          <Card title="Overview">
            <div className="grid gap-6 md:grid-cols-2">
              {sections.map((sec, idx) => (
                <Section key={idx} title={sec.title} items={sec.items} />
              ))}
            </div>

            <div className="mt-6 space-y-3">
              <h3 className="text-sm font-semibold">Motivation</h3>
              <div className="flex flex-wrap gap-2">
                {motivation.length ? (
                  motivation.map((m) => (
                    <span
                      key={m}
                      className="inline-flex items-center rounded border border-slate-200 bg-white px-2 py-1 text-xs capitalize"
                    >
                      {m.replace(/_/g, " ")}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-slate-500">
                    No motivation selected
                  </span>
                )}
              </div>
            </div>

            <div className="mt-6 grid gap-6 md:grid-cols-3">
              <InfoBlock
                title="Apply Knowledge Plan"
                text={e.applyKnowledgePlan}
              />
              <InfoBlock
                title="3–5 Year Projection"
                text={e.careerProjection}
              />
              <InfoBlock
                title="Skills to Strengthen"
                text={e.skillsToStrengthen}
              />
            </div>
          </Card>

          <Card title="Partner">
            {hasPartner ? (
              <div className="grid grid-cols-3 gap-3">
                <KV label="Partner" value={e.partner || "—"} />
                <KV label="Partner location" value={e.partnerLocation || "—"} />
                <KV
                  label="Is partner active"
                  value={
                    typeof e.isPartnerActive === "boolean"
                      ? e.isPartnerActive
                        ? "Yes"
                        : "No"
                      : "—"
                  }
                />
              </div>
            ) : (
              <div className="text-sm text-slate-500">
                Not a member of a partner
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card title="Training & Transition">
            <KV
              label="Prior training"
              value={e.hasPriorTraining ? "Yes" : "No"}
            />
            <KV
              label="Details"
              value={
                e.priorTrainingDetails !== "undefined"
                  ? e.priorTrainingDetails
                  : ""
              }
            />
            <KV
              label="Target industry"
              value={e.targetIndustry !== "undefined" ? e.targetIndustry : ""}
            />
            <KV
              label="Transition intent"
              value={e.transitionIntent ? "Yes" : "No"}
            />
            <KV
              label="Transition details"
              value={
                e.transitionDetails !== "undefined" ? e.transitionDetails : ""
              }
            />
            <Divider />
            <KV
              label="Networking interest"
              value={e.interestedInNetworking ? "Yes" : "No"}
            />
            <KV
              label="Additional expectations"
              value={
                e.additionalExpectations !== "undefined"
                  ? e.additionalExpectations
                  : ""
              }
            />
            <Divider />
            <KV
              label="Declaration accepted"
              value={e.declarationAccepted ? "Yes" : "No"}
            />
            <KV label="Signature" value={e.signatureFullName} />
            <KV label="Terms accepted" value={e.termsAccepted ? "Yes" : "No"} />
          </Card>

          <AssignMembershipCard enrollmentId={id} />

          <AssignCoursePanel
            userEmail={e.email}
            enrollmentId={id}
            adminEmail={"admin@bct.com"}
            membershipTitle={e.membershipTitle || e.courseSlug}
          />
        </div>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="border-b border-slate-200 px-4 py-3">
        <h2 className="text-sm font-semibold">{title}</h2>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Section({ title, items }) {
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold">{title}</h3>
      <div className="overflow-hidden rounded-md border border-slate-200">
        <dl className="divide-y divide-slate-200">
          {items.map(([label, value], i) => (
            <div
              key={i}
              className="grid grid-cols-3 items-center gap-2 px-3 py-2.5 text-sm"
            >
              <dt className="col-span-1 text-slate-500">{label}</dt>
              <dd className="col-span-2 break-words">{value || "—"}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

function InfoBlock({ title, text }) {
  return (
    <div className="rounded-md border border-slate-200 p-4">
      <div className="text-xs font-medium text-slate-500">{title}</div>
      <div className="mt-1.5 text-sm leading-relaxed whitespace-pre-wrap">
        {text || "—"}
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="text-sm">
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div
        className={`mt-1 ${
          value === "Not provided" ? "italic text-[11px]" : ""
        }`}
      >
        {value || "—"}
      </div>
    </div>
  );
}

function Divider() {
  return <div className="my-2 h-px w-full bg-slate-200" />;
}

function PageSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-full bg-slate-100 animate-pulse" />
          <div className="space-y-2">
            <div className="h-4 w-40 bg-slate-100 rounded animate-pulse" />
            <div className="h-3 w-64 bg-slate-100 rounded animate-pulse" />
          </div>
        </div>
        <div className="flex gap-2">
          <div className="h-9 w-28 bg-slate-100 rounded animate-pulse" />
          <div className="h-9 w-36 bg-slate-100 rounded animate-pulse" />
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <div className="md:col-span-2 h-[420px] rounded-lg border border-slate-200 bg-white animate-pulse" />
        <div className="h-[420px] rounded-lg border border-slate-200 bg-white animate-pulse" />
      </div>
    </div>
  );
}
